<template>
  <div
    class="flex min-w-0 flex-col px-3 pt-3 pb-0"
    data-test="voice-message-bubble"
  >
    <div class="flex min-w-0 items-start gap-3">
      <button
        class="w-10 h-10 text-white rounded-full bg-surf-brand-base-def flex justify-center items-center shrink-0"
        type="button"
        data-test="voice-message-play"
        @click.stop="onToggle"
      >
        <wui-icon
          :name="isPlaying ? 'pauseF' : 'playF'"
          class="w-4!"
        />
        <span class="sr-only select-none">{{ $t('chat.playVoiceMessage') }}</span>
      </button>

      <div class="flex min-w-0 w-full max-w-full flex-1 flex-col">
        <div class="flex min-w-0 w-full items-center">
          <audio-waveform
            :peaks="peaks"
            :progress="progress"
            :is-playing="isPlaying"
            @seek="onSeek"
          />
          <!-- TODO: Uncomment when transcribe feature is implemented -->
          <!-- <button
            class="relative ml-1.5 flex h-7 w-10 shrink-0 items-center justify-center overflow-visible rounded-8 bg-surf-bg-def px-2 py-1.5 text-heading-m-16-22 text-surf-brand-base-def disabled:opacity-100"
            type="button"
            data-test="voice-message-transcribe"
            :title="transcribeButtonTitle"
            :disabled="isTranscribing"
            :aria-busy="isTranscribing"
            :aria-expanded="transcript ? isTranscriptExpanded : undefined"
            @click="onTranscribe"
          >
            <perimeter-loader
              v-if="isTranscribing"
              :radius="8"
              :max-stroke-width="2"
            />
            <wui-icon
              v-if="transcript && !isTranscribing"
              :name="isTranscriptExpanded ? 'chevronUpLgM' : 'chevronDownLgM'"
              class="relative z-1"
            />
            <span
              v-else
              class="relative z-1"
              aria-hidden="true"
            >→А</span>
            <span class="sr-only select-none">{{ transcribeButtonTitle }}</span>
          </button> -->
        </div>

        <div class="shrink-0 pr-16 leading-4.5 text-label-r-12-18 text-fg-base-fg14 tabular-nums">
          {{ durationLabel }}
        </div>
      </div>
    </div>

    <p
      v-if="showTranscript"
      class="pt-0.5 pb-2 text-body-r-15-22 text-fg-base-fg3 wrap-break-word select-text"
      data-test="voice-message-transcript"
    >
      {{ transcript }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { WuiIcon } from '@wui/common-library'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useStoreContacts } from '@/entities/contact'

//import { PerimeterLoader } from '@/shared/components/loaders'
import { useLocalization } from '@/shared/composables/localization'
import { getUnknownContactLabel } from '@/shared/utils/contacts'

import { getVoiceMessagePeaks } from '../lib/extract-audio-peaks'
import { formatVoiceDuration } from '../lib/format-voice-duration'
import { WAVEFORM_BARS_COUNT } from '../model/constants'
import { useVoiceMessageStore } from '../model/store'
import { useVoicePlayer } from '../model/use-voice-player'

import AudioWaveform from './audio-waveform.vue'

const emit = defineEmits<{
  (event: 'resize'): void
}>()

const props = withDefaults(defineProps<{
  fileCookie: string
  pServed?: string
  eventId?: number
  isOutgoing?: boolean
  executor?: string
  durationMs?: number
}>(), {
  pServed: '',
  eventId: 0,
  isOutgoing: false,
  executor: '',
  durationMs: 0,
})

const { t } = useLocalization()
const { findByPServed } = useStoreContacts()
const store = useVoiceMessageStore()
const { playback, transcripts, /*transcribingCookies,*/ expandedTranscripts } = storeToRefs(store)
const { toggle, playTrack, seek } = useVoicePlayer()

const peaks = ref<number[]>(Array.from({ length: WAVEFORM_BARS_COUNT }, (_, index) => {
  return 0.2 + ((index * 17) % 13) / 30
}))
const decodedDurationMs = ref(props.durationMs)

const title = computed(() => {
  if (props.isOutgoing) return t('common.you')
  if (!props.executor) return t('mainAside.voiceMessage')
  return findByPServed(props.executor)?.name ?? getUnknownContactLabel(props.executor)
})

const track = computed(() => ({
  fileCookie: props.fileCookie,
  fileName: title.value,
  pServed: props.pServed,
  eventId: props.eventId,
}))

const isCurrent = computed(() => playback.value?.fileCookie === props.fileCookie)
const isPlaying = computed(() => Boolean(isCurrent.value && playback.value?.isPlaying))
const progress = computed(() => {
  if (!isCurrent.value) return 0
  const duration = playback.value?.duration || decodedDurationMs.value / 1000
  if (!duration) return 0
  return (playback.value?.currentTime ?? 0) / duration
})

const durationLabel = computed(() => {
  if (isCurrent.value && playback.value) {
    return formatVoiceDuration(playback.value.currentTime * 1000)
  }
  return formatVoiceDuration(decodedDurationMs.value)
})

//const isTranscribing = computed(() => transcribingCookies.value.has(props.fileCookie))
const transcript = computed(() => transcripts.value.get(props.fileCookie))
const isTranscriptExpanded = computed(() => expandedTranscripts.value.has(props.fileCookie))
const showTranscript = computed(() => Boolean(transcript.value && isTranscriptExpanded.value))
// const transcribeButtonTitle = computed(() => {
//   if (!transcript.value) return t('chat.transcribeVoiceMessage')
//   return isTranscriptExpanded.value ? t('common.collapse') : t('common.expand')
// })

const onToggle = () => {
  void toggle(track.value, decodedDurationMs.value / 1000)
}

// const onTranscribe = () => {
//   if (!props.fileCookie || isTranscribing.value) return
//   if (transcript.value) {
//     store.toggleTranscript(props.fileCookie)
//     return
//   }
//   void store.requestTranscript(props.fileCookie, t('chat.voiceMessageTranscriptMock'))
// }

const onSeek = async (ratio: number) => {
  const fallbackDuration = decodedDurationMs.value / 1000
  if (!isCurrent.value) {
    await playTrack(track.value, fallbackDuration * ratio, fallbackDuration)
    return
  }
  seek(ratio, fallbackDuration)
}

watch(() => props.fileCookie, async (fileCookie) => {
  if (!fileCookie) return

  try {
    const result = await getVoiceMessagePeaks(fileCookie)
    peaks.value = result.peaks
    decodedDurationMs.value = result.durationMs

    const current = playback.value
    if (current?.fileCookie === fileCookie && !current.duration && result.durationMs) {
      store.setPlayback({
        ...current,
        duration: result.durationMs / 1000,
      })
    }
  } catch {
    return
  }
}, { immediate: true })

watch(showTranscript, () => {
  emit('resize')
})
</script>
